import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { fetchContent } from "../../api/content";
import {
  completePaidReady,
  fetchLaunchPlan,
  fetchPaidReady,
  savePayment,
  setKycStatus,
  uploadKycScreenshot,
  type PaidReadyView,
} from "../../api/paidReady";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { ScreenHeader } from "../../components/ScreenHeader";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { useAsync } from "../../lib/useAsync";
import { LaunchPlanScreen } from "../../screens/paidReady/LaunchPlanScreen";
import { PaidReadyScreen } from "../../screens/paidReady/PaidReadyScreen";

export function PaidReadyRoute() {
  const navigate = useNavigate();
  const loaded = useAsync(() => Promise.all([fetchPaidReady(), fetchContent()]), []);
  const [view, setView] = useState<PaidReadyView | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setView(loaded.data[0]);
  }, [loaded.data]);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view && loaded.data ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <PaidReadyScreen
            view={view}
            payMethods={loaded.data[1].payMethods}
            uploadingPlatform={uploading}
            uploadError={uploadError}
            busy={busy}
            onPayment={(methods, confirmed) => {
              setView({
                ...view,
                step: { ...view.step, payMethods: methods, payConfirmed: confirmed },
              });
              void attempt(
                () => undefined,
                setError,
                async () => setView(await savePayment(methods, confirmed)),
              );
            }}
            onKycStatus={(platformId, status) =>
              void attempt(
                () => undefined,
                setError,
                async () => setView(await setKycStatus(platformId, status)),
              )
            }
            onKycFile={(platformId, file) => {
              setUploading(platformId);
              setUploadError(null);
              uploadKycScreenshot(platformId, file)
                .then(setView)
                .catch((e: unknown) => setUploadError(messageOf(e)))
                .finally(() => setUploading(null));
            }}
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completePaidReady());
                // Only the first completion opens the Launch Plan; re-saving a finished step stays here.
                if (!wasDone) void navigate("/launch-plan", { state: { justDone: true } });
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function LaunchPlanRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const loaded = useAsync(fetchLaunchPlan, []);
  const justDone = Boolean((location.state as { justDone?: boolean } | null)?.justDone);
  return (
    <ScreenState
      loading={loaded.loading}
      error={loaded.error}
      onRetry={loaded.reload}
      renderError={(e) => (
        <div>
          <ScreenHeader kicker="Launch Plan" title="Not yet." sub={e.message} />
          <Button onClick={() => void navigate("/gig/4")}>Go to Step 4</Button>
        </div>
      )}
    >
      {loaded.data ? <LaunchPlanScreen plan={loaded.data} justDone={justDone} /> : null}
    </ScreenState>
  );
}
